import {
  CellProps,
  Column,
  ConnectedIcon,
  DisconnectedIcon,
  MakeTableHeadCell,
  PaginationHook,
  TableColumnSortOrder,
  TableHeadCellGeneratorProps,
  TableWithPagination,
} from "@shared/primitive-components"
import { useCallback, useMemo } from "react"
import { FleetEntryWithMileage } from "../../../api/rebates/useGetFleetEntriesWithMileage"
import { buildMaybeObfuscatedHeader } from "../../../components/Obfuscated"

const ConnectedCell = ({ value }: CellProps<boolean, FleetEntryWithMileage>) => (
  <>{value ? <ConnectedIcon /> : <DisconnectedIcon />}</>
)

const MileageCell = ({ value, row }: CellProps<number | null, FleetEntryWithMileage>) => {
  let text = value ? value.toLocaleString() : `0`
  text += row.original.isConnected ? `mi` : `mi*`
  return <>{text}</>
}

type Props = Readonly<{
  page: ReadonlyArray<FleetEntryWithMileage>
  dataCount: number
  pagination: PaginationHook
  sort: { property: keyof FleetEntryWithMileage | undefined; order: TableColumnSortOrder | undefined } | undefined
  sortData: (property: keyof FleetEntryWithMileage, newSort: TableColumnSortOrder | undefined) => void
}>

export const MileageFleetListTable = ({ page, dataCount, sort, pagination, sortData }: Props) => {
  const SortedHeaderCell = useCallback(
    ({ header, property }: TableHeadCellGeneratorProps<keyof FleetEntryWithMileage>) => (
      <MakeTableHeadCell<keyof FleetEntryWithMileage>
        header={header}
        sort={{
          key: property,
          currentKey: sort?.property,
          currentOrder: sort?.order,
          fn: (order: TableColumnSortOrder | undefined) => sortData(property, order),
        }}
      />
    ),
    [sort, sortData]
  )
  const VRNHeader = useCallback(() => <SortedHeaderCell header="Registration" property="vrn" />, [SortedHeaderCell])
  const MakeModelHeader = useCallback(() => <SortedHeaderCell header="Vehicle" property="name" />, [SortedHeaderCell])
  const MileageHeader = useCallback(() => <SortedHeaderCell header="Mileage" property="mileage" />, [SortedHeaderCell])
  const ConnectedHeader = useCallback(
    () => <SortedHeaderCell header="Connected" property="isConnected" />,
    [SortedHeaderCell]
  )
  const columns = useMemo<Array<Column<FleetEntryWithMileage>>>(
    () => [
      buildMaybeObfuscatedHeader<FleetEntryWithMileage>(`vrn`, VRNHeader, { type: `vrn` }),
      {
        Header: MakeModelHeader,
        accessor: `name`,
      },
      {
        Header: MileageHeader,
        accessor: `mileage`,
        Cell: MileageCell,
      },
      {
        Header: ConnectedHeader,
        accessor: `isConnected`,
        Cell: ConnectedCell,
      },
    ],
    [ConnectedHeader, MakeModelHeader, MileageHeader, VRNHeader]
  )

  return (
    <TableWithPagination<FleetEntryWithMileage>
      data={page}
      dataCount={dataCount}
      pagination={pagination}
      columns={columns}
    />
  )
}
